import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  bindHumanProvenanceContinuation,
  compareHumanCardsCanonical,
  fetchHumanProvenance,
  humanEvidenceKey,
  normalizeHumanProvenanceRequest,
  type HumanCard,
  type HumanFacetBucket,
  type HumanGroupDimension,
  type HumanProvenanceEnvelope,
  type HumanProvenanceRequest,
  type HumanProvenanceResult,
  type HumanRootRef,
  type NormalizedHumanProvenanceRequest,
} from "../../api/humanProvenance";

export const HUMAN_PROVENANCE_MAX_RETAINED_PAGES = 8;
export const HUMAN_PROVENANCE_MAX_RETAINED_RECORDS = 800;

interface HumanPageReplay {
  cursor: string;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
}

export interface MergedHumanProvenance extends Omit<HumanProvenanceResult, "items" | "buckets"> {
  items: HumanCard[];
  buckets: HumanFacetBucket[];
  page_count: number;
}

export interface HumanProvenanceState {
  data: MergedHumanProvenance | null;
  isPending: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isExactSettled: boolean;
  hasNextPage: boolean;
  retentionLimitReached: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<unknown>;
}

export const HUMAN_PROVENANCE_FACET_DIMENSIONS = [
  "record_kind",
  "actor",
  "decision_status",
  "evidence_type",
  "entry_type",
  "sensitivity_tier",
  "lifecycle_state",
] as const satisfies readonly HumanGroupDimension[];

export interface HumanProvenanceFacetState {
  buckets: ReadonlyMap<HumanGroupDimension, readonly HumanFacetBucket[]>;
  isFetching: boolean;
  isExactSettled: boolean;
  retentionLimitReached: boolean;
  error: Error | null;
}

function rootKey(root: HumanRootRef | null): string {
  if (!root) return "";
  return root.entity_kind === "decision_evidence"
    ? humanEvidenceKey(root)
    : `${root.entity_kind}:${root.id}`;
}

function cardKey(card: HumanCard): string {
  return card.entity_kind === "decision_evidence"
    ? humanEvidenceKey(card.ref)
    : `${card.entity_kind}:${card.ref.id}`;
}

function exactJson(value: unknown): string {
  return JSON.stringify(value);
}

/** Merge cursor pages only after proving their full semantic and temporal closure. */
export function mergeHumanProvenancePages(
  pages: readonly HumanProvenanceEnvelope[],
): MergedHumanProvenance | null {
  const first = pages[0]?.data;
  if (!first) return null;
  if (pages.length > HUMAN_PROVENANCE_MAX_RETAINED_PAGES) {
    throw new Error(`Human provenance retained more than ${HUMAN_PROVENANCE_MAX_RETAINED_PAGES} pages.`);
  }
  const items: HumanCard[] = [];
  const buckets: HumanFacetBucket[] = [];
  const identities = new Set<string>();
  const bucketKeys = new Set<string>();
  let incomplete = first.temporal_history.status === "incomplete";
  for (const [pageIndex, envelope] of pages.entries()) {
    const page = envelope.data;
    if (page.project_id !== first.project_id
      || page.projection !== first.projection
      || rootKey(page.root_ref) !== rootKey(first.root_ref)
      || exactJson(page.temporal_cut) !== exactJson(first.temporal_cut)
      || exactJson(page.applied_selection) !== exactJson(first.applied_selection)) {
      throw new Error(`Human provenance cursor page ${pageIndex + 1} changed its project, mode, root, selection, or exact temporal cut.`);
    }
    if (pageIndex < pages.length - 1 && (!page.truncated || !page.next_cursor)) {
      throw new Error(`Human provenance cursor page ${pageIndex + 1} ended before a retained continuation.`);
    }
    for (const item of page.items) {
      const key = cardKey(item);
      if (identities.has(key)) throw new Error(`Human provenance cursor pages repeated ${key}.`);
      identities.add(key);
      items.push(item);
    }
    for (const bucket of page.buckets) {
      if (bucketKeys.has(bucket.key)) throw new Error(`Human provenance cursor pages repeated facet ${bucket.key}.`);
      bucketKeys.add(bucket.key);
      buckets.push(bucket);
    }
    incomplete ||= page.temporal_history.status === "incomplete";
  }
  if (items.length + buckets.length > HUMAN_PROVENANCE_MAX_RETAINED_RECORDS) {
    throw new Error(`Human provenance retained more than ${HUMAN_PROVENANCE_MAX_RETAINED_RECORDS} records.`);
  }
  const canonicalItems = [...items].sort(compareHumanCardsCanonical).map(cardKey);
  if (canonicalItems.some((key, index) => cardKey(items[index]!) !== key)) {
    throw new Error("Human provenance cursor pages broke canonical effective-time order.");
  }
  const canonicalBucketKeys = [...buckets].map((bucket) => bucket.key).sort();
  if (canonicalBucketKeys.some((key, index) => buckets[index]?.key !== key)) {
    throw new Error("Human provenance cursor pages broke canonical facet-key order.");
  }
  const last = pages[pages.length - 1]!.data;
  return {
    ...first,
    items,
    buckets,
    next_cursor: last.next_cursor,
    truncated: last.truncated,
    temporal_history: incomplete
      ? { status: "incomplete", marker: "temporal_history_incomplete" }
      : { status: "complete", marker: null },
    warnings: [
      ...(last.truncated ? ["human_provenance_page_truncated" as const] : []),
      ...(incomplete ? ["temporal_history_incomplete" as const] : []),
    ],
    page_count: pages.length,
  };
}

function continuation(
  request: NormalizedHumanProvenanceRequest,
  page: HumanPageReplay | undefined,
): HumanProvenanceRequest {
  return page ? { ...request, ...page } : request;
}

/**
 * Bounded human-provenance query with exact-cut cursor replay.
 *
 * A background first-page refetch intentionally exposes no previous document:
 * an unpinned request may settle at a new temporal cut, so showing the old one
 * would create a provenance flash. Appending a cursor page keeps the settled
 * document visible because the server-authenticated trio is unchanged.
 */
export function useHumanProvenance(
  request: HumanProvenanceRequest | null,
  enabled = true,
): HumanProvenanceState {
  const normalized = useMemo(
    () => request ? normalizeHumanProvenanceRequest(request) : null,
    [request],
  );
  const query = useInfiniteQuery({
    queryKey: ["thread-human-provenance-v1", normalized],
    enabled: enabled && normalized !== null,
    initialPageParam: undefined as HumanPageReplay | undefined,
    queryFn: ({ pageParam, signal }) => {
      if (!normalized) throw new Error("Human provenance request is unavailable.");
      return fetchHumanProvenance(continuation(normalized, pageParam), { signal });
    },
    getNextPageParam: (lastPage, pages): HumanPageReplay | undefined => {
      if (!lastPage.data.next_cursor || pages.length >= HUMAN_PROVENANCE_MAX_RETAINED_PAGES) return undefined;
      return {
        cursor: lastPage.data.next_cursor,
        as_of: lastPage.data.temporal_cut.effective_as_of,
        recorded_as_of: lastPage.data.temporal_cut.recorded_as_of,
        snapshot_token: lastPage.data.temporal_cut.snapshot_token,
      };
    },
    staleTime: 0,
    retry: false,
  });
  const merged = useMemo(
    () => mergeHumanProvenancePages(query.data?.pages ?? []),
    [query.data?.pages],
  );
  const isExactSettled = !query.isFetching || query.isFetchingNextPage;
  const retainedPages = query.data?.pages ?? [];
  const retentionLimitReached = Boolean(
    retainedPages.length === HUMAN_PROVENANCE_MAX_RETAINED_PAGES
      && retainedPages[retainedPages.length - 1]?.data.next_cursor,
  );
  return {
    data: isExactSettled ? merged : null,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isExactSettled,
    hasNextPage: query.hasNextPage,
    retentionLimitReached,
    error: query.error instanceof Error ? query.error : null,
    fetchNextPage: query.fetchNextPage,
  };
}

/** Build a continuation request for non-React consumers with the same binding rules. */
export function nextHumanProvenanceRequest(
  request: HumanProvenanceRequest,
  page: HumanProvenanceEnvelope,
): HumanProvenanceRequest {
  return bindHumanProvenanceContinuation(request, page);
}

async function fetchHumanFacet(
  request: NormalizedHumanProvenanceRequest,
  dimension: HumanGroupDimension,
  signal: AbortSignal,
): Promise<MergedHumanProvenance> {
  let current: HumanProvenanceRequest = {
    ...request,
    projection: "facet",
    root_ref: null,
    group_by: dimension,
    cursor: null,
  };
  const pages: HumanProvenanceEnvelope[] = [];
  while (pages.length < HUMAN_PROVENANCE_MAX_RETAINED_PAGES) {
    const page = await fetchHumanProvenance(current, { signal });
    pages.push(page);
    if (!page.data.next_cursor) break;
    current = bindHumanProvenanceContinuation(current, page);
  }
  const merged = mergeHumanProvenancePages(pages);
  if (!merged) throw new Error(`Human ${dimension} facet returned no page.`);
  return merged;
}

/** Permission-safe server facets, all bound to the same exact request cut. */
export function useHumanProvenanceFacets(
  request: HumanProvenanceRequest | null,
  enabled = true,
): HumanProvenanceFacetState {
  const normalized = useMemo(
    () => request ? normalizeHumanProvenanceRequest(request) : null,
    [request],
  );
  const queries = useQueries({
    queries: HUMAN_PROVENANCE_FACET_DIMENSIONS.map((dimension) => ({
      queryKey: ["thread-human-provenance-v1", "facet", dimension, normalized],
      enabled: enabled && normalized !== null,
      queryFn: ({ signal }: { signal: AbortSignal }) => {
        if (!normalized) throw new Error("Human provenance facet request is unavailable.");
        return fetchHumanFacet(normalized, dimension, signal);
      },
      staleTime: 0,
      retry: false,
    })),
  });
  const isFetching = queries.some((query) => query.isFetching);
  const firstError = queries.find((query) => query.error instanceof Error)?.error;
  const buckets = useMemo(() => {
    const result = new Map<HumanGroupDimension, readonly HumanFacetBucket[]>();
    if (isFetching) return result;
    HUMAN_PROVENANCE_FACET_DIMENSIONS.forEach((dimension, index) => {
      const data = queries[index]?.data;
      if (data) result.set(dimension, data.buckets);
    });
    return result;
  }, [isFetching, queries]);
  return {
    buckets,
    isFetching,
    isExactSettled: !isFetching && !firstError,
    retentionLimitReached: queries.some((query) => query.data?.truncated === true),
    error: firstError instanceof Error ? firstError : null,
  };
}
